import type { AgentRuntimeQuestionPrompt } from "./read-model";

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
}

function asString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeAllowMultiple(value: Record<string, unknown>): boolean {
  return (
    value.allowMultiple === true ||
    value.allow_multiple === true ||
    value.multiple === true ||
    value.multiSelect === true
  );
}

function normalizeAllowCustom(value: Record<string, unknown>): boolean {
  return value.allowCustom === true || value.custom === true;
}

function buildQuestionOptions(args: {
  rawOptions: unknown[];
  questionId: string;
}): AgentRuntimeQuestionPrompt["options"] {
  return args.rawOptions
    .map((entry, optionIndex) => {
      const option = asRecord(entry);
      const label = asString(option?.label) ?? asString(option?.name);
      if (!label) return null;

      return {
        id: asString(option?.id) ?? `${args.questionId}:option:${optionIndex}`,
        label,
        description: asString(option?.description) ?? undefined,
      };
    })
    .filter((option): option is NonNullable<typeof option> => option !== null);
}

function normalizeTopLevelFallback(args: {
  raw: Record<string, unknown>;
  requestId?: string | null;
}): AgentRuntimeQuestionPrompt | null {
  const prompt =
    asString(args.raw.prompt) ??
    asString(args.raw.question) ??
    asString(args.raw.header);
  if (!prompt) return null;

  const questionId = `${args.requestId ?? "ask-user"}:question:0`;
  const rawOptions = Array.isArray(args.raw.options) ? args.raw.options : [];
  const options = buildQuestionOptions({ rawOptions, questionId });
  if (options.length === 0) return null;

  return {
    id: questionId,
    prompt,
    header: asString(args.raw.header) ?? undefined,
    allowMultiple: normalizeAllowMultiple(args.raw),
    allowCustom: normalizeAllowCustom(args.raw),
    options,
  };
}

export interface NormalizedClaudeAskUserInput {
  title: string | null;
  questions: AgentRuntimeQuestionPrompt[];
}

export function normalizeClaudeAskUserInput(args: {
  input: unknown;
  requestId?: string | null;
}): NormalizedClaudeAskUserInput | null {
  const rawInput = asRecord(args.input);
  if (!rawInput) return null;

  const rawQuestions = Array.isArray(rawInput.questions) ? rawInput.questions : [];
  const questions = rawQuestions
    .map((entry, questionIndex) => {
      const question = asRecord(entry);
      if (!question) return null;

      const prompt =
        asString(question.prompt) ??
        asString(question.question) ??
        asString(question.header) ??
        `Question ${questionIndex + 1}`;
      const questionId =
        asString(question.id) ??
        `${args.requestId ?? "ask-user"}:question:${questionIndex}`;

      const rawOptions = Array.isArray(question.options) ? question.options : [];
      const options = buildQuestionOptions({ rawOptions, questionId });
      if (options.length === 0) return null;

      return {
        id: questionId,
        prompt,
        header: asString(question.header) ?? asString(question.title) ?? undefined,
        allowMultiple: normalizeAllowMultiple(question),
        allowCustom: normalizeAllowCustom(question),
        options,
      };
    })
    .filter((question): question is NonNullable<typeof question> => question !== null);

  if (questions.length > 0) {
    return {
      title: asString(rawInput.title),
      questions,
    };
  }

  const fallbackQuestion = normalizeTopLevelFallback({
    raw: rawInput,
    requestId: args.requestId,
  });
  if (!fallbackQuestion) return null;

  return {
    title: asString(rawInput.title),
    questions: [fallbackQuestion],
  };
}

function getAnswerKeyFromQuestionRecord(args: {
  rawQuestion: unknown;
  fallbackPrompt: string;
}): string {
  const question = asRecord(args.rawQuestion);
  return (
    asString(question?.question) ??
    asString(question?.prompt) ??
    asString(question?.header) ??
    args.fallbackPrompt
  );
}

export function buildClaudeAskUserUpdatedInput(args: {
  input: unknown;
  normalizedInput: NormalizedClaudeAskUserInput | null;
  requestId: string;
  selectedByQuestionId: Record<string, string[]>;
}): Record<string, unknown> | null {
  const baseInput = asRecord(args.input);
  if (!baseInput || !args.normalizedInput) return null;

  const rawQuestions = Array.isArray(baseInput.questions) ? baseInput.questions : null;
  const answers = Object.fromEntries(
    args.normalizedInput.questions
      .map((question, questionIndex) => {
        const questionId = question.id ?? `${args.requestId}:question:${questionIndex}`;
        const selectedOptionIds = args.selectedByQuestionId[questionId] ?? [];
        if (selectedOptionIds.length === 0) return null;

        const selectedLabels = question.options
          .map((option, optionIndex) => ({
            id: option.id ?? `${questionId}:option:${optionIndex}`,
            label: option.label,
          }))
          .filter((option) => selectedOptionIds.includes(option.id))
          .map((option) => option.label);
        if (selectedLabels.length === 0) return null;

        const answerKey = rawQuestions
          ? getAnswerKeyFromQuestionRecord({
              rawQuestion: rawQuestions[questionIndex],
              fallbackPrompt: question.prompt,
            })
          : getAnswerKeyFromQuestionRecord({
              rawQuestion: baseInput,
              fallbackPrompt: question.prompt,
            });

        return [
          answerKey,
          question.allowMultiple ? selectedLabels.join(", ") : selectedLabels[0] ?? "",
        ] as const;
      })
      .filter((entry): entry is readonly [string, string] => entry !== null),
  );

  if (Object.keys(answers).length === 0) return null;

  return {
    ...baseInput,
    answers,
  };
}
